import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../App.css';
 
function Header() {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [hideTop, setHideTop] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const navRef = useRef();
  const aboutRef = useRef();
  const orgRef = useRef();
  const eventsRef = useRef();
  const insightsRef = useRef();
 
  useEffect(() => {
    setHideTop(false);
  }, []);
 
  // Close everything on route change
  useEffect(() => {
    setNavOpen(false);
    setDropdownOpen({});
    document.body.classList.remove('menu-open');
  }, [location.pathname]);

  // Desktop only: close dropdown when clicking outside
  useEffect(() => {
    if (window.innerWidth <= 900) return; // skip on mobile
    function handleClickOutside(e) {
      const refs = { about: aboutRef, org: orgRef, events: eventsRef, insights: insightsRef };
      Object.keys(dropdownOpen).forEach((key) => {
        if (
          dropdownOpen[key] &&
          refs[key]?.current &&
          !refs[key].current.contains(e.target)
        ) {
          setDropdownOpen((prev) => ({ ...prev, [key]: false }));
        }
      });
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);
 
  // Mobile: toggle only the clicked dropdown, never touch others
  const handleDropdown = (key) => {
    if (window.innerWidth <= 900) {
      setDropdownOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };
 
  const closeMenu = () => {
    setNavOpen(false);
    setDropdownOpen({});
  };
 
  // Desktop: open on hover
  const handleDropdownMouseEnter = (key) => {
    if (window.innerWidth > 900) {
      setDropdownOpen((prev) => ({ ...prev, [key]: true }));
    }
  };
 
  // Desktop: close on mouse leave
  const handleDropdownMouseLeave = (key) => {
    if (window.innerWidth > 900) {
      setDropdownOpen((prev) => ({ ...prev, [key]: false }));
    }
  };
 
  // When a dropdown link is clicked: close only that dropdown, close nav, navigate
  const handleDropdownItemClick = (key) => {
    setDropdownOpen((prev) => ({ ...prev, [key]: false }));
    setNavOpen(false);
    if (document.activeElement) document.activeElement.blur();
  };
 
  return (
    <>
      {navOpen && <div className="nav-overlay" onClick={closeMenu}></div>}
      {!hideTop && (
        <div className="sub-header">
          <div></div>
          <div className="sub-header-actions">
      <Link
  to="/admin/customerlogin"
  className="customer-login-link"
  target="_blank"
  rel="noopener noreferrer"
>
  Customer Login
</Link>
 <span className="divider">|</span>
<Link to="/search-organization" className="search-link">
  Search Your Organization
</Link>
            <span className="divider"></span>
          </div>
        </div>
      )}
      <header className={`main-header sticky-nav${hideTop ? ' shadow' : ''}`}>
        <div className="logo-section">
          <Link to="/">
            <img src="/New_logo.jpg" alt="Outstanding Workplaces Logo" className="logo" />
          </Link>
        </div>
        {!navOpen && (
          <button
            className="hamburger"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
          >
            <span />
            <span />
            <span />
          </button>
        )}
        {navOpen && (
          <button className="menu-close-btn" onClick={closeMenu} aria-label="Close Menu">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M8 8L20 20M20 8L8 20" stroke="#002071" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        )}
        <div className="nav-actions">
          <nav className={`main-nav${navOpen ? ' open' : ''}`} ref={navRef}>
            <Link to="/" className={location.pathname === "/" ? "active" : ""} onClick={closeMenu}>
              Home
            </Link>
 
            {/* About Us dropdown */}
            <div
              className={`nav-dropdown${dropdownOpen['about'] ? ' open' : ''}`}
              ref={aboutRef}
              onMouseEnter={() => handleDropdownMouseEnter('about')}
              onMouseLeave={() => handleDropdownMouseLeave('about')}
            >
              <span
                className="dropdown-toggle"
                onClick={() => handleDropdown('about')}
                tabIndex={0}
                role="button"
                aria-expanded={dropdownOpen['about'] ? "true" : "false"}
              >
                About Us
                <span className="header-arrow"></span>
              </span>
              {dropdownOpen['about'] && (
                <div className="dropdown-content">
                  <Link to="/what-we-do" onClick={() => handleDropdownItemClick('about')}>What We Do</Link>
                  <Link to="/team" onClick={() => handleDropdownItemClick('about')}>Our Team</Link>
                </div>
              )}
            </div>
 
            {/* Core Offerings dropdown */}
            <div
              className={`nav-dropdown${dropdownOpen['org'] ? ' open' : ''}`}
              ref={orgRef}
              onMouseEnter={() => handleDropdownMouseEnter('org')}
              onMouseLeave={() => handleDropdownMouseLeave('org')}
            >
              <span
                className="dropdown-toggle"
                onClick={() => handleDropdown('org')}
                tabIndex={0}
                role="button"
                aria-expanded={dropdownOpen['org'] ? "true" : "false"}
              >
                Core Offerings
                <span className="header-arrow"></span>
              </span>
              {dropdownOpen['org'] && (
                <div className="dropdown-content">
                  <Link to="/organization-framework" onClick={() => handleDropdownItemClick('org')}>Organization Framework</Link>
                  <Link to="/our-methodology" onClick={() => handleDropdownItemClick('org')}>Our Methodology</Link>
                  <Link to="/get-certified" onClick={() => handleDropdownItemClick('org')}>Get Certified</Link>
                  <Link to="/voice" onClick={() => handleDropdownItemClick('org')}>Voice Of Employees</Link>
                </div>
              )}
            </div>
 
            <Link
              to="/certified/micro"
              className={location.pathname.startsWith("/certified") ? "active" : ""}
              onClick={closeMenu}
            >
              Certified Organizations
            </Link>
 
            {/* Events dropdown */}
            <div
              className={`nav-dropdown${dropdownOpen['events'] ? ' open' : ''}`}
              ref={eventsRef}
              onMouseEnter={() => handleDropdownMouseEnter('events')}
              onMouseLeave={() => handleDropdownMouseLeave('events')}
            >
              <span
                className="dropdown-toggle"
                onClick={() => handleDropdown('events')}
                tabIndex={0}
                role="button"
                aria-expanded={dropdownOpen['events'] ? "true" : "false"}
              >
                Events
                <span className="header-arrow"></span>
              </span>
              {dropdownOpen['events'] && (
                <div className="dropdown-content">
                  <Link to="/upcoming-events" onClick={() => handleDropdownItemClick('events')}>Upcoming Events</Link>
                  <Link to="/gallery" onClick={() => handleDropdownItemClick('events')}>Event Gallery</Link>
                </div>
              )}
            </div>
 
            {/* Insights dropdown */}
            <div
              className={`nav-dropdown${dropdownOpen['insights'] ? ' open' : ''}`}
              ref={insightsRef}
              onMouseEnter={() => handleDropdownMouseEnter('insights')}
              onMouseLeave={() => handleDropdownMouseLeave('insights')}
            >
              <span
                className={`dropdown-toggle${location.pathname.startsWith("/blogs") || location.pathname.startsWith("/news") ? " active" : ""}`}
                onClick={() => handleDropdown('insights')}
                tabIndex={0}
                role="button"
                aria-expanded={dropdownOpen['insights'] ? "true" : "false"}
              >
                Insights
                <span className="header-arrow"></span>
              </span>
              {dropdownOpen['insights'] && (
                <div className="dropdown-content">
                  <Link to="/blogs" onClick={() => handleDropdownItemClick('insights')}>Blogs</Link>
                  <Link to="/news" onClick={() => handleDropdownItemClick('insights')}>News</Link>
                  <Link to="/chro-interviews" onClick={() => handleDropdownItemClick('insights')}>CHRO Interviews</Link>
                </div>
              )}
            </div>
 
            <Link
              to="/contact"
              className={location.pathname === "/contact" ? "active" : ""}
              onClick={closeMenu}
            >
              Contact Us
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
}
 
export default Header;